import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { REPORT_TYPES } from '../../types/reportTypes';

interface Annotation {
  id: string;
  x: number;
  y: number;
  title: string;
  description: string;
}

const ANNOTATIONS: Annotation[] = [
  {
    id: 'patient-info',
    x: 12,
    y: 5,
    title: 'Patient Information',
    description: 'Contains patient demographics including name, age, gender, and medical record number.'
  },
  {
    id: 'specimen-info',
    x: 52,
    y: 5,
    title: 'Specimen Information',
    description: 'Details about the sample including collection date, type, and specimen source.'
  },
  {
    id: 'complexity-score',
    x: 20,
    y: 12,
    title: 'Complexity Score',
    description: 'Assessment of overall infection severity and resistance complexity.'
  },
  {
    id: 'primary-treatment',
    x: 28,
    y: 17,
    title: 'Primary Treatment Recommendation',
    description: 'Outlines the most appropriate first-line antibiotic regimen, including dosage, duration, and route, based on the infection source, identified pathogens, and resistance profile.'
  },
  {
    id: 'detected-pathogen',
    x: 80,
    y: 12,
    title: 'Detected Pathogen',
    description: 'Identified microorganism with associated category and clinical priority.'
  },
  {
    id: 'resistance-profile',
    x: 79,
    y: 19,
    title: 'Resistance Profile',
    description: 'Resistance genes and associated score indicating the extent of antimicrobial resistance.'
  },
  {
    id: 'alternative-treatments',
    x: 24,
    y: 38,
    title: 'Alternative Treatment Options',
    description: 'Other antibiotics that can be used with adverse effect scores to guide safer selection.'
  },
  {
    id: 'allergies-detected',
    x: 78,
    y: 40,
    title: 'Allergies Detected',
    description: 'Allergies detected in the patient.'
  },
  {
    id: 'monitoring-plan',
    x: 77,
    y: 44,
    title: 'Monitoring Plan',
    description: 'Checklist for monitoring side effects and complications during treatment.'
  },
  {
    id: 'qr-code',
    x: 4,
    y: 88,
    title: 'Scan QR Code',
    description: 'Each report features a unique QR code that gives providers access to an interactive version, offering expanded information, detailed explanations, and direct links to relevant references.'
  },
  {
    id: 'footer-note',
    x: 19,
    y: 88,
    title: 'Clinical Disclaimer',
    description: 'Advisory note that final treatment decisions must be made by a licensed provider.'
  }
];

interface PopupPosition {
  top: number;
  left: number;
}

interface InteractiveReportViewerProps {
  selectedReportType?: string;
}

const InteractiveReportViewer: React.FC<InteractiveReportViewerProps> = ({ 
  selectedReportType = 'uti_ud_112' 
}) => {
  const [selectedAnnotation, setSelectedAnnotation] = useState<Annotation | null>(null);
  const [popupPosition, setPopupPosition] = useState<PopupPosition>({ 
    top: 0, 
    left: 0 
  });
  const [currentReportType, setCurrentReportType] = useState(selectedReportType);
  
  const containerRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  // Get current report type info
  const reportType = REPORT_TYPES.find(type => type.id === currentReportType);

  const categoryColors = {
    respiratory: 'bg-blue-100 text-blue-800 border-blue-200',
    gastrointestinal: 'bg-green-100 text-green-800 border-green-200',
    urinary: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    wound: 'bg-red-100 text-red-800 border-red-200'
  };

  useEffect(() => {
    if (selectedAnnotation && containerRef.current) {
      const container = containerRef.current;
      const containerRect = container.getBoundingClientRect();
      
      // Calculate position based on annotation coordinates
      const left = containerRect.left + (selectedAnnotation.x / 100) * containerRect.width;
      const top = containerRect.top + (selectedAnnotation.y / 100) * containerRect.height;
      
      // Adjust position to keep popup within viewport
      const popupWidth = 256; // w-64 = 16rem = 256px
      const popupHeight = 200; // estimated height
      
      let adjustedLeft = left;
      let adjustedTop = top;
      
      if (left + popupWidth > window.innerWidth) {
        adjustedLeft = left - popupWidth;
      }
      
      if (top + popupHeight > window.innerHeight) {
        adjustedTop = top - popupHeight;
      }
      
      setPopupPosition({ top: adjustedTop, left: adjustedLeft });
    }
  }, [selectedAnnotation]);

  return (
    <div className="w-full">
      {/* Report Type Selector */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-primary mb-3 text-center">Select Report Type</h3>
        <div className="flex flex-wrap justify-center gap-2">
          {REPORT_TYPES.map((type) => (
            <button
              key={type.id}
              onClick={() => setCurrentReportType(type.id)}
              className={`px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                currentReportType === type.id
                  ? 'bg-primary text-white'
                  : 'bg-background-alt text-text hover:bg-primary/10 border border-border'
              }`}
            >
              {type.name}
            </button>
          ))}
        </div>
        
        {reportType && (
          <div className="mt-3 text-center">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${categoryColors[reportType.category]}`}>
              {reportType.category.charAt(0).toUpperCase() + reportType.category.slice(1)} • {reportType.panelSize} tests • {reportType.specimenType}
            </span>
          </div>
        )}
      </div>

      <div ref={containerRef} className="relative w-full max-w-4xl mx-auto">
        <div className="relative">
          <img
            src="/Clinical Microbiology Report_Sample-1.png"
            alt={`${reportType?.name || 'Clinical Microbiology'} Report Sample`}
            className="w-full h-auto rounded-lg shadow-md"
          />
          
          {/* Annotation Dots */}
          {ANNOTATIONS.map((annotation) => (
            <button
              key={annotation.id}
              className="absolute w-5 h-5 rounded-full bg-primary hover:bg-primary/80 transition-colors shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary/50 annotation-dot"
              style={{
                left: `${annotation.x}%`,
                top: `${annotation.y}%`,
                transform: 'translate(-50%, -50%)'
              }}
              onClick={() => setSelectedAnnotation(annotation)}
              aria-label={`Learn more about ${annotation.title}`}
            />
          ))}
        </div>

        {/* Annotation Popup */}
        <AnimatePresence>
          {selectedAnnotation && (
            <motion.div
              ref={popupRef}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              style={{
                position: 'fixed',
                top: popupPosition.top,
                left: popupPosition.left,
                zIndex: 50
              }}
              className="w-64 p-5 bg-background rounded-lg shadow-xl border-t-4 border-primary"
            >
              <h3 className="text-xl font-semibold text-primary mb-3">
                {selectedAnnotation.title}
              </h3>
              <p className="text-sm text-text leading-relaxed">
                {selectedAnnotation.description}
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Report Details */}
        {reportType && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            className="mt-6 p-4 bg-background-alt rounded-lg border border-border"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-lg font-bold text-primary">{reportType.panelSize}</div>
                <div className="text-sm text-secondary">Total Tests</div>
              </div>
              <div>
                <div className="text-lg font-bold text-primary">{reportType.specimenType}</div>
                <div className="text-sm text-secondary">Specimen Type</div>
              </div>
              <div>
                <button
                  onClick={() => window.open(reportType.pdfPath, '_blank')}
                  className="bg-primary text-white px-4 py-2 rounded-md hover:bg-primary/90 transition-colors text-sm"
                >
                  Download PDF Sample
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default InteractiveReportViewer; 